import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

interface Worker {
  id: number;
  name: string;
  trade: string;
  status: 'active' | 'break' | 'completed';
  location: string;
  progress: number;
  pathProgress: number;
}

const GPSTrackingPage = () => {
  // SEO Configuration
  useSEO({
    title: 'GPS Tracking for Tradies Australia | Real-Time Workforce Management | MtaPlannerPro',
    description: 'Track your team in real-time with GPS tracking for Australian tradies. Live location tracking, automated timesheets, route playback, geofencing, and travel reports. See where workers are, eliminate time theft, and improve job efficiency.',
    keywords: 'GPS tracking tradies, workforce tracking Australia, real-time location tracking, tradie GPS app, automated timesheets, geofencing jobs, route tracking, mobile workforce management, construction GPS tracking',
    canonical: '/gps-tracking',
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'MtaPlannerPro GPS Tracking',
        applicationCategory: 'BusinessApplication',
        applicationSubCategory: 'GPS Tracking & Workforce Management',
        operatingSystem: 'Web, iOS, Android',
        description: 'Real-time GPS tracking for Australian trade businesses. Track worker locations, automate timesheets, replay routes, set geofences, and monitor job progress.',
        featureList: [
          'Real-time GPS location tracking',
          'Automated timesheet generation',
          'Route playback and history',
          'Geofencing for job sites',
          'Live alerts and notifications',
          'Travel time and distance reports',
          'Mobile app for workers',
          'Battery-efficient tracking',
          'Offline mode with sync',
          'Privacy controls'
        ],
        offers: {
          '@type': 'Offer',
          price: '99',
          priceCurrency: 'AUD',
          priceValidUntil: '2026-12-31',
          availability: 'https://schema.org/InStock'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How GPS Tracking Works for Tradies',
        description: 'Simple 4-step process to track your team with GPS',
        totalTime: 'PT5M',
        step: [
          {
            '@type': 'HowToStep',
            position: 1,
            name: 'Install Mobile App',
            text: 'Workers download the MTA Planner Pro app on their phones'
          },
          {
            '@type': 'HowToStep',
            position: 2,
            name: 'Clock In',
            text: 'Workers clock in when they start their day. GPS tracking begins automatically'
          },
          {
            '@type': 'HowToStep',
            position: 3,
            name: 'Live Tracking',
            text: 'You see real-time locations, job progress, and route history on your dashboard'
          },
          {
            '@type': 'HowToStep',
            position: 4,
            name: 'Auto Reports',
            text: 'Timesheets, travel logs, and job reports are generated automatically'
          }
        ]
      }
    ]
  });

  const [activeWorkers, setActiveWorkers] = useState(12);
  const [selectedWorker, setSelectedWorker] = useState<number | null>(1);
  const [isPlaying, setIsPlaying] = useState(true);

  const workers: Worker[] = [
    { id: 1, name: 'Jake Morrison', trade: 'Electrician', status: 'active', location: 'Bondi Junction', progress: 65, pathProgress: 0 },
    { id: 2, name: 'Tom Chen', trade: 'Plumber', status: 'active', location: 'Parramatta', progress: 42, pathProgress: 0 },
    { id: 3, name: 'Sarah Williams', trade: 'Carpenter', status: 'break', location: 'Chatswood', progress: 78, pathProgress: 0 },
    { id: 4, name: 'Mike Roberts', trade: 'Electrician', status: 'active', location: 'Liverpool', progress: 33, pathProgress: 0 },
    { id: 5, name: 'Emma Davis', trade: 'Painter', status: 'completed', location: 'Penrith', progress: 100, pathProgress: 0 },
  ];

  const [workerStates, setWorkerStates] = useState(workers);

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setWorkerStates(prev => prev.map(worker => ({
        ...worker,
        pathProgress: (worker.pathProgress + 0.5) % 100
      })));
    }, 50);

    return () => clearInterval(interval);
  }, [isPlaying]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveWorkers(prev => prev + (Math.random() > 0.5 ? 1 : -1));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-emerald-500';
      case 'break': return 'bg-amber-500';
      case 'completed': return 'bg-slate-400';
      default: return 'bg-slate-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'On Site';
      case 'break': return 'On Break';
      case 'completed': return 'Day Complete';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
              <i className="ri-tools-fill text-white text-xl"></i>
            </div>
            <div>
              <div className="font-bold text-slate-900 text-lg leading-none">MTA Planner Pro</div>
              <div className="text-xs text-slate-500">GPS Tracking</div>
            </div>
          </Link>
          <Link to="/demo" className="px-6 py-2.5 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors whitespace-nowrap">
            Book Demo
          </Link>
        </div>
      </nav>

      {/* Live Status Banner */}
      <div className="fixed top-[73px] left-0 right-0 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-3 z-40">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-center gap-3">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          <span className="font-semibold">{activeWorkers} workers active right now</span>
          <span className="text-emerald-100">• Live tracking across Sydney</span>
        </div>
      </div>

      {/* Hero Section */}
      <div className="pt-32 pb-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium mb-6">
            <i className="ri-map-pin-line"></i>
            Real-Time GPS Tracking
          </div>
          <h1 className="text-5xl font-bold text-slate-900 mb-6">
            Know Where Your Team Is,<br />Every Second of the Day
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            Live GPS tracking, automated timesheets, and route playback. See exactly where your workers are, track job progress, and eliminate time theft.
          </p>
        </div>
      </div>

      {/* Main Demo Section */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Map View */}
          <div className="lg:col-span-2 bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden">
            <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h3 className="font-semibold text-slate-900">Live Map View</h3>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <i className="ri-map-2-line"></i>
                  Sydney Metro Area
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                >
                  <i className={isPlaying ? 'ri-pause-line' : 'ri-play-line'}></i>
                  {isPlaying ? 'Pause' : 'Play'}
                </button>
              </div>
            </div>

            {/* Animated Map */}
            <div className="relative h-[600px] bg-gradient-to-br from-slate-100 to-slate-200">
              {/* Map Grid */}
              <svg className="absolute inset-0 w-full h-full opacity-20">
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-slate-400"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>

              {/* Sydney Map Outline (Simplified) */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 600">
                {/* Coastline */}
                <path
                  d="M 200 100 Q 250 120 300 100 L 350 150 Q 400 180 450 160 L 500 200 Q 520 220 500 250 L 480 300 Q 460 350 420 380 L 380 420 Q 340 450 300 440 L 250 420 Q 200 400 180 360 L 160 300 Q 150 250 170 200 Z"
                  fill="none"
                  stroke="#94a3b8"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  className="opacity-40"
                />
                
                {/* Roads */}
                <line x1="200" y1="300" x2="500" y2="300" stroke="#cbd5e1" strokeWidth="3" className="opacity-30" />
                <line x1="350" y1="150" x2="350" y2="450" stroke="#cbd5e1" strokeWidth="3" className="opacity-30" />
                <line x1="250" y1="200" x2="450" y2="400" stroke="#cbd5e1" strokeWidth="2" className="opacity-20" />
              </svg>

              {/* Worker Pins with Animated Paths */}
              {workerStates.map((worker, index) => {
                const positions = [
                  { x: 300, y: 200, path: 'M 300 200 Q 350 250 400 300 Q 420 350 380 400' },
                  { x: 450, y: 250, path: 'M 450 250 Q 400 280 350 320 Q 320 360 300 400' },
                  { x: 280, y: 350, path: 'M 280 350 Q 320 320 360 300 Q 400 280 440 300' },
                  { x: 380, y: 180, path: 'M 380 180 Q 360 220 340 260 Q 320 300 300 340' },
                  { x: 500, y: 320, path: 'M 500 320 Q 460 340 420 360 Q 380 380 340 400' },
                ];

                const pos = positions[index % positions.length];
                const isSelected = selectedWorker === worker.id;

                return (
                  <g key={worker.id}>
                    {/* Animated Path Trail */}
                    {isSelected && (
                      <path
                        d={pos.path}
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="3"
                        strokeDasharray="10,5"
                        className="opacity-60"
                        style={{
                          strokeDashoffset: -worker.pathProgress * 5,
                          transition: 'stroke-dashoffset 0.05s linear'
                        }}
                      />
                    )}

                    {/* Worker Pin */}
                    <g
                      onClick={() => setSelectedWorker(worker.id)}
                      className="cursor-pointer"
                      style={{
                        transform: `translate(${pos.x}px, ${pos.y}px)`,
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      {/* Pulse Ring */}
                      {worker.status === 'active' && (
                        <circle
                          cx="0"
                          cy="0"
                          r="20"
                          fill="none"
                          stroke="#10b981"
                          strokeWidth="2"
                          className="animate-ping opacity-75"
                        />
                      )}

                      {/* Pin Shadow */}
                      <ellipse
                        cx="0"
                        cy="35"
                        rx="12"
                        ry="4"
                        fill="#000000"
                        className="opacity-20"
                      />

                      {/* Pin Body */}
                      <path
                        d="M 0 -25 Q -15 -25 -15 -10 Q -15 5 0 30 Q 15 5 15 -10 Q 15 -25 0 -25 Z"
                        fill={worker.status === 'active' ? '#10b981' : worker.status === 'break' ? '#f59e0b' : '#94a3b8'}
                        stroke="#ffffff"
                        strokeWidth="2"
                        className={isSelected ? 'drop-shadow-lg' : ''}
                      />

                      {/* Worker Initial */}
                      <text
                        x="0"
                        y="-5"
                        textAnchor="middle"
                        fill="#ffffff"
                        fontSize="14"
                        fontWeight="bold"
                      >
                        {worker.name.charAt(0)}
                      </text>

                      {/* Info Card on Hover */}
                      {isSelected && (
                        <g transform="translate(25, -30)">
                          <rect
                            x="0"
                            y="0"
                            width="160"
                            height="80"
                            rx="8"
                            fill="#ffffff"
                            stroke="#e2e8f0"
                            strokeWidth="1"
                            className="drop-shadow-xl"
                          />
                          <text x="12" y="22" fontSize="13" fontWeight="600" fill="#0f172a">
                            {worker.name}
                          </text>
                          <text x="12" y="40" fontSize="11" fill="#64748b">
                            {worker.trade}
                          </text>
                          <text x="12" y="56" fontSize="11" fill="#64748b">
                            📍 {worker.location}
                          </text>
                          <rect
                            x="12"
                            y="64"
                            width="136"
                            height="6"
                            rx="3"
                            fill="#f1f5f9"
                          />
                          <rect
                            x="12"
                            y="64"
                            width={worker.progress * 1.36}
                            height="6"
                            rx="3"
                            fill="#10b981"
                          />
                        </g>
                      )}
                    </g>
                  </g>
                );
              })}

              {/* Legend */}
              <div className="absolute bottom-6 left-6 bg-white rounded-lg shadow-lg p-4 border border-slate-200">
                <div className="text-xs font-semibold text-slate-900 mb-3">Worker Status</div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                    On Site
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                    On Break
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <div className="w-3 h-3 bg-slate-400 rounded-full"></div>
                    Completed
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Worker List Sidebar */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
              <h3 className="font-semibold text-slate-900">Active Workers</h3>
              <p className="text-sm text-slate-600 mt-1">{workerStates.filter(w => w.status === 'active').length} on site</p>
            </div>
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {workerStates.map(worker => (
                <div
                  key={worker.id}
                  onClick={() => setSelectedWorker(worker.id)}
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedWorker === worker.id ? 'bg-emerald-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-medium text-slate-900">{worker.name}</div>
                      <div className="text-sm text-slate-600">{worker.trade}</div>
                    </div>
                    <div className={`px-2 py-1 ${getStatusColor(worker.status)} text-white text-xs rounded-full font-medium whitespace-nowrap`}>
                      {getStatusText(worker.status)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                    <i className="ri-map-pin-line text-xs"></i>
                    {worker.location}
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs text-slate-600">
                      <span>Day Progress</span>
                      <span className="font-medium">{worker.progress}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                        style={{ width: `${worker.progress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Everything You Need to Track Your Team</h2>
            <p className="text-lg text-slate-600">Powerful GPS tracking features built for trade businesses</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-route-line',
                title: 'Route Playback',
                description: 'Replay any worker\'s entire day route. See every job site visited, travel time, and stops made.'
              },
              {
                icon: 'ri-time-line',
                title: 'Auto Timesheets',
                description: 'GPS data automatically generates accurate timesheets. No more manual time tracking or disputes.'
              },
              {
                icon: 'ri-notification-3-line',
                title: 'Live Alerts',
                description: 'Get notified when workers arrive on site, leave early, or deviate from planned routes.'
              },
              {
                icon: 'ri-map-pin-range-line',
                title: 'Geofencing',
                description: 'Set up virtual boundaries around job sites. Track arrivals, departures, and time spent on site.'
              },
              {
                icon: 'ri-smartphone-line',
                title: 'Mobile App',
                description: 'Workers use the mobile app for clock in/out. Lightweight, battery-efficient GPS tracking.'
              },
              {
                icon: 'ri-bar-chart-box-line',
                title: 'Travel Reports',
                description: 'Detailed reports on travel time, distance, fuel costs, and route efficiency for every worker.'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${feature.icon} text-2xl text-emerald-600`}></i>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">How GPS Tracking Works</h2>
            <p className="text-lg text-slate-600">Simple setup, powerful results</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Install Mobile App', description: 'Workers download the MTA Planner Pro app on their phones' },
              { step: '2', title: 'Clock In', description: 'Workers clock in when they start their day. GPS tracking begins automatically' },
              { step: '3', title: 'Live Tracking', description: 'You see real-time locations, job progress, and route history on your dashboard' },
              { step: '4', title: 'Auto Reports', description: 'Timesheets, travel logs, and job reports are generated automatically' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile App Preview */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-medium mb-6">
                <i className="ri-smartphone-line"></i>
                Mobile App
              </div>
              <h2 className="text-4xl font-bold text-white mb-6">
                GPS Tracking in Your Workers' Pockets
              </h2>
              <p className="text-xl text-slate-300 mb-8">
                Lightweight mobile app with battery-efficient GPS tracking. Workers clock in/out, view their schedule, and update job status—all from their phone.
              </p>
              <div className="space-y-4">
                {[
                  'One-tap clock in/out with GPS verification',
                  'View daily schedule and job locations',
                  'Update job status and add photos',
                  'Offline mode syncs when back online',
                  'Battery-efficient background tracking'
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 text-slate-200">
                    <i className="ri-checkbox-circle-fill text-emerald-400 text-xl"></i>
                    {feature}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              {/* Phone Mockup */}
              <div className="relative mx-auto w-[300px] h-[600px] bg-slate-900 rounded-[3rem] border-8 border-slate-800 shadow-2xl overflow-hidden">
                {/* Phone Screen */}
                <div className="absolute inset-0 bg-white">
                  {/* Status Bar */}
                  <div className="bg-slate-900 text-white px-6 py-3 flex items-center justify-between text-xs">
                    <span>9:41</span>
                    <div className="flex items-center gap-1">
                      <i className="ri-signal-wifi-fill"></i>
                      <i className="ri-battery-fill"></i>
                    </div>
                  </div>

                  {/* App Header */}
                  <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-6 py-4">
                    <div className="text-sm opacity-90">Good morning</div>
                    <div className="text-xl font-bold">Jake Morrison</div>
                  </div>

                  {/* Clock In Button */}
                  <div className="p-6">
                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white text-center shadow-lg">
                      <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                        <i className="ri-map-pin-line text-4xl"></i>
                        <div className="absolute inset-0 rounded-full border-4 border-white/40 animate-ping"></div>
                      </div>
                      <div className="text-2xl font-bold mb-2">Clock In</div>
                      <div className="text-sm opacity-90">Tap to start your day</div>
                    </div>
                  </div>

                  {/* Today's Jobs */}
                  <div className="px-6">
                    <div className="text-sm font-semibold text-slate-900 mb-3">Today's Jobs</div>
                    <div className="space-y-3">
                      {[
                        { time: '8:00 AM', client: 'Smith Residence', location: 'Bondi', status: 'active' },
                        { time: '11:30 AM', client: 'Office Fitout', location: 'CBD', status: 'pending' }
                      ].map((job, index) => (
                        <div key={index} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-medium text-slate-900 text-sm">{job.client}</div>
                            <div className={`w-2 h-2 rounded-full ${job.status === 'active' ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}></div>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-600">
                            <span><i className="ri-time-line"></i> {job.time}</span>
                            <span><i className="ri-map-pin-line"></i> {job.location}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-6 -right-6 bg-white rounded-xl shadow-xl p-4 border border-slate-200 animate-bounce">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                  <span className="font-medium text-slate-900">GPS Active</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">
            Ready to Track Your Team in Real-Time?
          </h2>
          <p className="text-xl text-slate-600 mb-8">
            See exactly where your workers are, eliminate time theft, and automate timesheets with GPS tracking.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/demo" className="px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap">
              Book a Demo
            </Link>
            <Link to="/" className="px-8 py-4 bg-slate-100 text-slate-900 rounded-lg font-semibold hover:bg-slate-200 transition-colors whitespace-nowrap">
              Learn More
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center text-slate-600">
          <p>&copy; 2025 MTA Planner Pro. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default GPSTrackingPage;